import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * CORE-BJ responsibility 6 — "disclose the access to the people it affected".
 *
 * Reads `get_my_support_access_disclosures()` (migration 20260901120000), a
 * read-only, self-scoped authority that takes NO parameters: the server derives
 * scope from `auth.uid()` alone, so there is nothing here for a caller to spoof.
 *
 * The RPC is not present in the generated `types.ts` (that file is generated
 * from the live database), so the call uses the repository's established
 * `(supabase as any).rpc(...)` convention with a locally declared row shape.
 */

/** Which entitlement surfaced a disclosure row. */
export type SupportAccessScope = 'own_account' | 'shop';

/**
 * Honest lifecycle state of the access.
 *
 * `expired` is deliberately distinct from `ended`: the session's bound lapsed,
 * so the access is over, but no scheduled cleanup closed the row
 * (CORE-BJ-HARD-001), so the exact end time was never recorded. Collapsing it
 * into `ended` would require inventing a timestamp; collapsing it into `active`
 * would overstate. It is neither.
 */
export type SupportAccessStatus = 'active' | 'ended' | 'expired' | 'unknown';

export interface SupportAccessDisclosure {
  notice_id: string;
  shop_id: string;
  shop_name: string | null;
  scope: SupportAccessScope;
  mode: string;
  /** The disclosure-safe reason recorded with the notice. Never the operator's internal note. */
  reason: string | null;
  operator_label: string;
  started_at: string;
  ended_at: string | null;
  status: SupportAccessStatus;
}

export function useSupportAccessDisclosures() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['support-access-disclosures', user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<SupportAccessDisclosure[]> => {
      const { data, error } = await (supabase as any).rpc(
        'get_my_support_access_disclosures',
      );
      // Surface the failure. A caller must never be able to read a failed load
      // as "no support access occurred" — that is the exact confusion this
      // capability exists to prevent.
      if (error) throw error;
      return (data ?? []) as SupportAccessDisclosure[];
    },
  });
}
